import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { routes } from './app.routes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LucideAngularModule, icons } from 'lucide-angular';

import { authInterceptor } from './core/interceptors/auth.interceptor';
import { baseUrlInterceptor } from './core/interceptors/base-url.interceptor';
import { API_BASE_MAESTRO_CALLE, API_BASE_URL, APP_CONFIG, AppConfig, applyFavicon, applyTheme, resolveBrandingUrl } from './core/app-config.token';
import { environment } from '../environments/environment';

export const configRef: { value: AppConfig | null } = { value: null };

// Branding propio de SmartDOM (no de la municipalidad): igual para todos los
// clientes, así que vive en el frontend en vez de en el config por cliente.
const SMARTDOM_BRANDING: AppConfig['smartDOM'] = {
  name: 'SmartDOM',
  logoBlack: 'assets/solnet/smartdom.png',
  logoWhite: 'assets/solnet/smartdom-white.png',
};

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-CL' },

    provideZoneChangeDetection({ eventCoalescing: true }),

    provideRouter(routes, withHashLocation()),

    provideHttpClient(withInterceptors([authInterceptor, baseUrlInterceptor])),

    {
      provide: APP_INITIALIZER,
      multi: true,
      // api.baseUrl viene del build (environment.apiBaseUrl, ver angular.json
      // → configurations "independencia-*"), no del propio config: es lo único
      // que el frontend necesita saber de antemano para preguntarle al backend
      // de ESA municipalidad todo lo demás (tema, datos del proyecto, feature
      // flags, límites de subida) vía /public/frontend-config.
      useFactory: () => () =>
        fetch(`${environment.apiBaseUrl.replace(/\/+$/, '')}/public/frontend-config`, { cache: 'no-store' })
          .then(r => r.json())
          .then(config => {
            config.api = { ...config.api, baseUrl: environment.apiBaseUrl };
            config.smartDOM = SMARTDOM_BRANDING;

            // logo/banner/favicon: rutas relativas al backend de esta municipalidad
            // (api.baseUrl), no URLs fijas — así siguen automáticamente al ambiente/cliente.
            config.project.logo = resolveBrandingUrl(config.project?.logo, config.api?.baseUrl);
            config.project.banner = resolveBrandingUrl(config.project?.banner, config.api?.baseUrl);
            config.project.favicon = resolveBrandingUrl(config.project?.favicon, config.api?.baseUrl);
            configRef.value = config;
            applyTheme(config.theme);
            applyFavicon(config.project.favicon);
          })
    },

    { provide: APP_CONFIG,            useFactory: () => configRef.value },
    { provide: API_BASE_URL,          useFactory: () => environment.apiBaseUrl },
    { provide: API_BASE_MAESTRO_CALLE, useFactory: () => configRef.value?.api?.maestroCalleUrl },

    provideAnimationsAsync(),

    MessageService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: Aura,
        options: { darkModeSelector: false },
      },
    }),
    importProvidersFrom(
      LucideAngularModule.pick(icons)
    ),
  ],
};
